'use client';
import React, { useState } from 'react';
import {Modal, SlideOver, Button, Card, Heading} from '@ui';
import { withOptionalId } from '@lib/withOptionalId';
import { Section, Container } from '@layout';
import MarkupSnippet from '../markup-snippet/MarkupSnippet';

interface OverlayDemoProps {
  sectionID?: string;
  classes?: string;
  containerId?: string;
  containerClasses?: string;
  isSpanish?: boolean;
}
const OverlayDemo: React.FC<OverlayDemoProps> = ({
  isSpanish = false,
  sectionID = '',
  classes = '',
  containerId,
  containerClasses,
}) => {
  const t = (en: string, es: string) => (isSpanish ? es : en);
  const [modalOpen, setModalOpen] = useState(false);
  const [slideOpen, setSlideOpen] = useState(false);

  return (
    <Section {...withOptionalId(sectionID)} className={classes}>
      <Container containerId={containerId} containerClasses={containerClasses}>
        <Heading as="h3">{t('Modals & Overlays', 'Modales y Overlays')}</Heading>
        <p className='sub full'>
          {t(
            'Centered modal and slide-over overlay. Keyboard: ESC to close. Focus is trapped inside.',
            'Modal centrado y panel lateral. Teclado: ESC para cerrar. El foco queda atrapado dentro.'
          )}
        </p>
        <Card>
          <div className='row mt-16'>
            <Button
              label={t('Open centered modal', 'Abrir modal centrado')}
              className='btn-primary'
              onClick={() => setModalOpen(true)}
            />
            <Button
              label={t('Open slide-over', 'Abrir panel lateral')}
              className='btn-secondary'
              onClick={() => setSlideOpen(true)}
            />
          </div>
        </Card>
        <MarkupSnippet
          label={t('HTML', 'HTML')}
          copyLabel={t('Copy', 'Copiar')}
          copiedLabel={t('Copied', 'Copiado')}
          code={`<div class="overlay" role="dialog" aria-modal="true">
  <div class="overlay-backdrop"></div>
  <div class="card modal-window size-md" role="document">
    <header class="modal-header">
      <h3 class="modal-title">${t('Schedule a consult', 'Programar una consulta')}</h3>
      <button class="btn btn-close" type="button" aria-label="Close"></button>
    </header>
    <div class="modal-body">
      <p>${t('Tell us a bit about your study.', 'Cuentanos un poco sobre tu estudio.')}</p>
    </div>
    <footer class="modal-footer">
      <button class="btn btn-ghost" type="button"><span class="label">${t('Cancel', 'Cancelar')}</span></button>
      <button class="btn btn-primary" type="button"><span class="label">${t('Send', 'Enviar')}</span></button>
    </footer>
  </div>
</div>`}
        />
      </Container>
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={t('Schedule a consult', 'Programar una consulta')}
        id='modal-centered'
        actions={
          <>
            <Button label={t('Cancel', 'Cancelar')} className='btn-ghost' onClick={() => setModalOpen(false)} />
            <Button label={t('Send', 'Enviar')} className='btn-primary' />
          </>
        }
      >
        <p className='sub full'>
          {t(
            'Tell us a bit about your study. We’ll follow up within 1–2 business days.',
            'Cuentanos un poco sobre tu estudio. Te contactaremos en 1-2 dias habiles.'
          )}
        </p>
        <div className='mt-16'>
          <fieldset>
            <span>{t('Work email', 'Correo laboral')}</span>
            <input type='email' placeholder='alex@company.com' autoFocus />
          </fieldset>
          <fieldset>
            <span>{t('Target start', 'Inicio objetivo')}</span>
            <input type='date' />
          </fieldset>
          <fieldset>
            <span>{t('Notes', 'Notas')}</span>
            <textarea className='textarea' rows={3} placeholder={t('Scope, sites, systems…', 'Alcance, sedes, sistemas…')} />
          </fieldset>
        </div>
      </Modal>
      <SlideOver
        open={slideOpen}
        onClose={() => setSlideOpen(false)}
        title={t('Study details', 'Detalles del estudio')}
        id='modal-slideover'
        actions={
          <>
            <Button label={t('Close', 'Cerrar')} className='btn-ghost' onClick={() => setSlideOpen(false)} />
            <Button label={t('Save', 'Guardar')} className='btn-primary' />
          </>
        }
      >
        <p className='sub full'>{t('Quick edit panel for metadata / settings.', 'Panel rapido para editar metadatos y configuracion.')}</p>
        <fieldset>
            <span>{t('Sponsor', 'Patrocinador')}</span>
          <input type='text' placeholder='TulioSolorzano' />
        </fieldset>
        <fieldset>
            <span>{t('Phase', 'Fase')}</span>
          <select className='select'>
            <option>{t('Phase I', 'Fase I')}</option>
            <option>{t('Phase II', 'Fase II')}</option>
            <option>{t('Phase III', 'Fase III')}</option>
          </select>
        </fieldset>
        <fieldset>
          <label className='switch'>
            <input type='checkbox' role='switch' />
            <span className='track'>
              <span className='thumb'></span>
            </span>
            <span className='switch-label'>{t('Enable alerts', 'Activar alertas')}</span>
          </label>
        </fieldset>
      </SlideOver>
    </Section>
  );
};

export default OverlayDemo;
